import { FC, useMemo, useState } from 'react';
import { TableNew } from '@dynamika-shared/ui/TableNew/TableNew';
import { NoContent } from '@dynamika-shared/ui/NoContent/NoContent';
import { PMTaskDefinition } from '@dynamika-shared/types/pmTaskDefinitions';
import { TableProps } from 'antd';
import { makeTaskDefinitionColumns } from './TaskDefinitionTable.columns';
import {
  sortTaskDefinitions,
  TaskDefinitionSortState,
} from './TaskDefinitionTable.utils';
import './TaskDefinitionTable.scss';

type TaskDefinitionTableViewProps = {
  data?: PMTaskDefinition[] | null;
  loading?: boolean;
  isPM: boolean;
  onEdit: (task: PMTaskDefinition) => void;
  onDelete: (task: PMTaskDefinition) => void;
  onCreate?: () => void;
};

export const TaskDefinitionTableView: FC<TaskDefinitionTableViewProps> = ({
  data,
  loading,
  isPM,
  onEdit,
  onDelete,
  onCreate,
}) => {
  const [sortState, setSortState] = useState<TaskDefinitionSortState>();
  const rows = Array.isArray(data) ? data : [];

  const sortedData = useMemo(
    () => sortTaskDefinitions(rows, sortState),
    [rows, sortState]
  );

  const columns = useMemo(
    () =>
      makeTaskDefinitionColumns(
        { edit: onEdit, delete: onDelete, sortState },
        isPM
      ),
    [isPM, onDelete, onEdit, sortState]
  );

  const emptyState = !isPM ? (
    <NoContent
      action={onCreate}
      actionText='Add Task Definition'
      iconName='noContent'
      title={
        <span className='text-1424 text-spanishGray'>
          No{' '}
          <span className='font-semibold text-chineseBlack'>
            Task Definitions
          </span>{' '}
          here yet! <br /> Click the button to create a Task Definition!
        </span>
      }
    />
  ) : (
    <div className='py-10 text-center text-dimGray'>
      No task definitions available.
    </div>
  );

  const handleChange: TableProps<PMTaskDefinition>['onChange'] = (
    _pagination,
    _filters,
    sorter
  ) => {
    const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter;

    if (!currentSorter?.columnKey || !currentSorter.order) {
      setSortState(undefined);
      return;
    }

    const order = currentSorter.order === 'ascend' ? 'asc' : 'desc';
    setSortState(`${String(currentSorter.columnKey)},${order}`);
  };

  const scrollConfig = sortedData.length > 0 ? { x: 1600 } : undefined;

  return (
    <div className='task-definition-table-wrapper'>
      <TableNew<PMTaskDefinition>
        rowKey='id'
        tableClassName='main-table no-content-transparent task-definition-table'
        columns={columns}
        dataSource={sortedData}
        loading={loading}
        pagination={false}
        scroll={scrollConfig}
        onChange={handleChange}
        locale={{ emptyText: emptyState }}
        showSorterTooltip={false}
      />
    </div>
  );
};
